import { Film, Music } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'

/** textフィールドからURLを抽出する（多くのアプリはURLをtextで送る） */
const extractUrl = (text: string): string => {
  const match = text.match(/https?:\/\/[^\s]+/)
  return match?.[0] ?? text
}

type ShareAction = {
  path: string
  label: string
  icon: typeof Film
  variant?: 'default' | 'secondary'
}

const ALL_ACTIONS: ShareAction[] = [
  { path: PATHS.CUSTOM_VIDEO, label: 'カスタム動画としてダウンロード', icon: Film, variant: 'default' },
  { path: PATHS.CUSTOM_MUSIC, label: 'カスタム音楽としてダウンロード', icon: Music, variant: 'secondary' },
]

export const SharePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const accessiblePages = useAppStore((state) => state.accessiblePages)

  const rawUrl = searchParams.get('url') ?? ''
  const sharedTitle = searchParams.get('title') ?? ''
  const sharedText = searchParams.get('text') ?? ''

  // URLが直接渡されない場合、textフィールドからURL抽出を試みる
  const sharedUrl = rawUrl || extractUrl(sharedText)

  // ユーザーのパーミッションに基づいてアクション候補をフィルタ
  const availableActions = useMemo(() => {
    const pagePaths = new Set(accessiblePages.map((p) => p.path))
    return ALL_ACTIONS.filter((action) => pagePaths.has(action.path))
  }, [accessiblePages])

  // 選択肢が1つしかない場合は自動で遷移
  if (availableActions.length === 1) {
    const target = `${availableActions[0].path}?url=${encodeURIComponent(sharedUrl)}`
    navigate(target, { replace: true })
    return null
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header title="共有受信" onClose={() => navigate(PATHS.HOME)} />

      <div className="flex flex-1 flex-col gap-6 p-4">
        {/* 共有情報 */}
        <div className="rounded-lg border bg-card p-4">
          {sharedTitle && <p className="mb-2 text-sm font-medium text-muted-foreground">{sharedTitle}</p>}
          <p className="break-all text-sm">{sharedUrl}</p>
        </div>

        {/* アクションボタン */}
        <div className="flex flex-col gap-3">
          {availableActions.map((action) => (
            <Button
              key={action.path}
              size="lg"
              variant={action.variant}
              className="w-full justify-start gap-3"
              onClick={() => navigate(`${action.path}?url=${encodeURIComponent(sharedUrl)}`)}
            >
              <action.icon size={20} />
              {action.label}
            </Button>
          ))}
        </div>

        {availableActions.length === 0 && (
          <p className="text-center text-sm text-muted-foreground">利用可能なアクションがありません</p>
        )}
      </div>
    </div>
  )
}
